import React, { useState, useContext, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CardMedia from "@mui/material/CardMedia";
import Rating from "@mui/material/Rating";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

import EditIcon from "@mui/icons-material/Edit";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";

import { capitalCase } from "capital-case";
import NumberFormat from "react-number-format";
import { Carousel } from "antd";

import curved9 from "assets/images/upstyled/default_background.png";

import colors from "assets/theme/base/colors";

import CategoryContext from "context/category-context";
import moment from "moment";
import Checkbox from "@mui/material/Checkbox";
import { canResizeImageUrl, getStyleImages } from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

function StyleCard({
  product,
  onDeleteStyle,
  onEditStyle,
  sx,
  size = "md",
  handleSelectStyle,
  isSelected,
  onEditRecord,
  collectionsInfo = [],
  imageMode = "normal",
}) {
  const [categoryLabel, setCategoryLabel] = useState(""); //Using Category Name if the category ID was found at category tree
  const [isHovered, setIsHovered] = useState(false);

  const {
    tree,
    actions: { findNodeByID },
  } = useContext(CategoryContext);

  useEffect(() => {
    let category = product?.type || "No Category";
    if (typeof category == "object") {
      category = category?.value;
    } else {
      const categoryNode = findNodeByID(tree, category);
      category = categoryNode?.label || category;
    }

    setCategoryLabel(category);
  }, [product]);

  const styleMeta = product?.style ? product.style : {};
  let styleImages = getStyleImages(product, true);

  const styleNumber = styleMeta?.style_number ? styleMeta.style_number : "N/A";

  const styles = {
    md: {
      cardWidth: 260,
      cardHeight: 400,
      imgHeight: 220,
      titleMinHeight: "34px",
      titleFontSize: "h6",
      textFontSize: "body1",
    },
    sm: {
      cardWidth: 160,
      cardHeight: 300,
      imgHeight: 150,
      titleMinHeight: "26px",
      titleFontSize: "body1",
      textFontSize: "body2",
    },
  };

  const ListRow = ({ label, value, divider = false }) => {
    return (
      <SuiBox px={1}>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center">
          <SuiTypography
            variant={styles[size].textFontSize}
            fontWeight="bold"
            textTransform="capitalize"
            mr={0.5}
            sx={{ flex: "none", minWidth: "min-content" }}
          >
            {label}
          </SuiTypography>

          <SuiBox>
            <Tooltip title={value}>
              <SuiTypography
                variant={styles[size].textFontSize}
                fontWeight="regular"
                color="text"
                sx={{
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {value}
              </SuiTypography>
            </Tooltip>
          </SuiBox>
        </SuiBox>

        {divider ? <Divider sx={{ backgroundColor: colors.dark.main, margin: "4px 2px" }} /> : null}
      </SuiBox>
    );
  };

  let collectionText = "N/A";
  if (product?.collection?.title) {
    collectionText = product.collection.title;
  } else if (collectionsInfo.length > 0 && product?.collection_id) {
    const findCollection = collectionsInfo.find((x) => x.id == product.collection_id);
    collectionText = findCollection.title;
  }

  return (
    <Card
      sx={{
        my: 2,
        mx: 1,
        width: styles[size].cardWidth,
        height: styles[size].cardHeight,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "visible",
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        ...sx,
      }}
    >
      <Carousel
        arrows={true}
        dots={false}
        prevArrow={
          <div>
            <button
              class="react-multiple-carousel__arrow react-multiple-carousel__arrow--left "
              type="button"
            ></button>
          </div>
        }
        nextArrow={
          <div>
            <button
              class="react-multiple-carousel__arrow react-multiple-carousel__arrow--right "
              type="button"
            ></button>
          </div>
        }
      >
        {styleImages.map((img) => (
          <SuiBox
            position="relative"
            width="100.25%"
            shadow="xl"
            borderRadius="xl"
            sx={{ overflow: "hidden" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ImageCardWrapper
              data={img}
              height={styles[size].imgHeight}
              width={styles[size].cardWidth}
            />

            {isHovered ? (
              <SuiBox position="absolute" bottom="4px" sx={{ width: "100%" }}>
                <SuiBox display="flex" gap={1} justifyContent="center" alignItems="center">
                  {onEditStyle ? (
                    <SuiButton
                      variants="contained"
                      size="small"
                      color="secondary"
                      sx={{ p: 1 }}
                      onClick={onEditStyle}
                    >
                      Edit
                    </SuiButton>
                  ) : null}
                  {onDeleteStyle ? (
                    <SuiButton
                      variants="contained"
                      size="small"
                      color="error"
                      sx={{ p: 1 }}
                      onClick={onDeleteStyle}
                    >
                      Delete
                    </SuiButton>
                  ) : null}
                </SuiBox>
              </SuiBox>
            ) : null}
          </SuiBox>
        ))}
      </Carousel>
      <SuiBox sx={{ position: "absolute", right: 10, top: 10 }}>
        <Tooltip title={styleNumber}>
          <SuiBadge
            variant="contained"
            color="secondary"
            badgeContent={
              <SuiTypography
                variant={styles.textFontSize}
                textTransform="none"
                color="secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {styleNumber}
              </SuiTypography>
            }
          />
        </Tooltip>
      </SuiBox>
      <SuiBox py={1} px={1}>
        <div>
          <SuiBox display="flex" justifyContent="space-between" alignItems="top">
            <Tooltip title={product.title}>
              <SuiTypography
                variant={styles[size].titleFontSize}
                fontWeight="medium"
                lineHeight="1.2"
                sx={{
                  width: styles[size].cardWidth,
                  minHeight: styles[size].titleMinHeight,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-all",
                }}
              >
                {product.title}
              </SuiTypography>
            </Tooltip>
            {handleSelectStyle ? (
              <Checkbox onChange={handleSelectStyle} checked={isSelected} />
            ) : null}
          </SuiBox>
          <ListRow label="Brand:" value={styleMeta?.brand || "N/A"} divider={true} />

          <ListRow label="Category:" value={categoryLabel || "N/A"} divider={true} />
          <SuiBox px={1}>
            <SuiBox display="flex" justifyContent="space-between" alignItems="center">
              <SuiBox>
                <SuiTypography
                  variant={styles[size].textFontSize}
                  fontWeight="bold"
                  textTransform="capitalize"
                  mr={0.5}
                >
                  Quantity:
                </SuiTypography>
              </SuiBox>
              <SuiBox>
                <SuiBox display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title={styleMeta?.quantity}>
                    <SuiTypography
                      variant={styles[size].textFontSize}
                      fontWeight="regular"
                      color="text"
                      sx={{
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {styleMeta?.quantity}
                    </SuiTypography>
                  </Tooltip>
                  <EditIcon
                    fontSize="sm"
                    sx={{ cursor: "pointer" }}
                    className="hover:text-blue-500"
                    onClick={() => onEditRecord("quantity")}
                  />
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </SuiBox>
          <Divider sx={{ backgroundColor: colors.dark.main, margin: "4px 2px" }} />
          <SuiBox px={1}>
            <SuiBox display="flex" justifyContent="space-between" alignItems="center">
              <SuiBox>
                <SuiTypography
                  variant={styles[size].textFontSize}
                  fontWeight="bold"
                  textTransform="capitalize"
                  mr={0.5}
                >
                  Target Unit Cost:
                </SuiTypography>
              </SuiBox>
              <SuiBox>
                <SuiBox display="flex" alignItems="center" gap={0.5}>
                  <Tooltip
                    title={
                      <NumberFormat
                        value={styleMeta.cost_per_unit}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"$"}
                        renderText={(value, props) => (
                          <SuiTypography
                            {...props}
                            variant={styles[size].textFontSize}
                            fontWeight="regular"
                            color="text"
                          >
                            {value}
                          </SuiTypography>
                        )}
                      />
                    }
                  >
                    <SuiTypography
                      variant={styles[size].textFontSize}
                      fontWeight="regular"
                      color="text"
                    >
                      <NumberFormat
                        value={styleMeta.cost_per_unit}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"$"}
                        renderText={(value, props) => (
                          <SuiTypography
                            variant={styles[size].textFontSize}
                            fontWeight="regular"
                            color="text"
                            {...props}
                            sx={{
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: "1",
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {value}
                          </SuiTypography>
                        )}
                      />
                    </SuiTypography>
                  </Tooltip>
                  <EditIcon
                    fontSize="sm"
                    sx={{ cursor: "pointer" }}
                    onClick={() => onEditRecord("cost_per_unit")}
                    className="hover:text-blue-500"
                  />
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </div>
      </SuiBox>
    </Card>
  );
}

export default StyleCard;
